import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import TerminalOutlinedIcon from '@mui/icons-material/TerminalOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';

import ChatBox from './components/ChatBox';
import SPARQLQuery from './components/SPARQLQuery';
import RDFView from './components/RDFView';
import ETLInterface from './components/ETLInterface';
import KGToText from './components/KGToText';
import ServiceStatus from './components/ServiceStatus';
import Traceability from './components/Traceability';
import MappingTab from './components/traceability/tabs/MappingTab';
import { apiService } from './api/apiClient';
import { useTheme } from './theme/ThemeContext';

const LOCALES = ['CA', 'ES', 'EN'];
const SIDEBAR_COLLAPSED_KEY = 'ubxat_sidebar_collapsed';

const SECTIONS = [
  { id: 'graph',        navKey: 'nav:graph',        contentKey: 'content:graphTitle',        Icon: HubOutlinedIcon },
  { id: 'queries',      navKey: 'nav:queries',      contentKey: 'content:queriesTitle',      Icon: TerminalOutlinedIcon },
  { id: 'chat',         navKey: 'nav:chat',         contentKey: 'content:chatTitle',         Icon: ChatBubbleOutlineOutlinedIcon },
  { id: 'etl',          navKey: 'nav:etl',          contentKey: 'content:etlTitle',          Icon: CloudUploadOutlinedIcon },
  { id: 'articles',     navKey: 'nav:articles',     contentKey: 'content:articlesTitle',     Icon: ArticleOutlinedIcon },
  { id: 'mapping',      navKey: 'nav:mapping',      contentKey: 'content:mappingTitle',      Icon: SchemaOutlinedIcon },
  { id: 'traceability', navKey: 'nav:traceability', contentKey: 'content:traceabilityTitle', Icon: TimelineOutlinedIcon },
  { id: 'status',       navKey: 'nav:status',       contentKey: 'content:statusTitle',       Icon: MonitorHeartOutlinedIcon },
];

function LanguageSwitcher() {
  const { i18n: i18nInstance } = useTranslation();
  const current = i18nInstance.language.toUpperCase();

  const handleChange = (loc) => {
    const lower = loc.toLowerCase();
    i18nInstance.changeLanguage(lower);
    try { localStorage.setItem('ubxat_locale', lower); } catch (_) {}
  };

  return (
    <div className="flex items-center gap-3 px-6 py-4 border-t border-border-subtle">
      {LOCALES.map((loc, i) => (
        <React.Fragment key={loc}>
          <button
            onClick={() => handleChange(loc)}
            className={`text-label font-medium tracking-widest transition-colors ${
              current === loc
                ? 'text-accent-text'
                : 'text-ink-secondary hover:text-ink'
            }`}
          >
            {loc}
          </button>
          {i < LOCALES.length - 1 && (
            <span className="text-ink-muted text-label">·</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function ThemeSwitcher({ collapsed }) {
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const label = isDark ? t('ui:switchToLight') : t('ui:switchToDark');
  return (
    <div className={`flex items-center py-4 border-t border-border-subtle ${collapsed ? 'justify-center px-0' : 'px-6'}`}>
      <button
        onClick={toggleTheme}
        aria-label={label}
        title={label}
        className="text-lg text-ink-secondary hover:text-accent-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-text focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised rounded"
      >
        {isDark ? '\u2600' : '\u263e'}
      </button>
    </div>
  );
}

function CollapseToggle({ collapsed, onToggle }) {
  const { t } = useTranslation();
  const label = collapsed ? t('ui:expandNav') : t('ui:collapseNav');
  const Icon = collapsed ? ChevronRightOutlinedIcon : ChevronLeftOutlinedIcon;
  return (
    <div className={`hidden sm:flex items-center py-3 border-t border-border-subtle ${collapsed ? 'justify-center px-0' : 'justify-end px-4'}`}>
      <button
        onClick={onToggle}
        aria-label={label}
        title={label}
        className="flex h-7 w-7 items-center justify-center rounded text-ink-secondary hover:text-accent-text hover:bg-surface-hover/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-text focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised"
      >
        <Icon sx={{ fontSize: 18 }} />
      </button>
    </div>
  );
}

function Sidebar({ active, onNavigate, isOpen, onClose, collapsed, onToggleCollapse }) {
  const { t } = useTranslation();

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-ink/30 z-30 sm:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <nav
        className={`
          fixed sm:sticky top-0 left-0 h-screen z-40 flex-shrink-0
          bg-surface-raised border-r border-border-subtle flex flex-col
          transition-[width,transform] duration-200
          ${collapsed ? 'w-14' : 'w-44'}
          ${isOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
        `}
        aria-label="Navigation"
      >
        <div className={`flex items-center py-5 border-b border-border-subtle ${collapsed ? 'justify-center px-0' : 'px-6'}`}>
          <span className="text-label font-semibold tracking-[0.1em] text-ink uppercase truncate">
            {collapsed ? t('ui:brand').slice(0, 1) : t('ui:brand')}
          </span>
        </div>

        <ul className="flex-1 py-4 space-y-0.5 overflow-y-auto">
          {SECTIONS.map((s) => {
            const isActive = active === s.id;
            const Icon = s.Icon;
            return (
              <li key={s.id}>
                <button
                  onClick={() => { onNavigate(s.id); onClose?.(); }}
                  aria-current={isActive ? 'page' : undefined}
                  title={collapsed ? t(s.navKey) : undefined}
                  className={`w-full flex items-center gap-3 py-3 text-sm transition-colors relative rounded-r-md ${
                    collapsed ? 'justify-center px-0' : 'text-left px-6'
                  } ${
                    isActive
                      ? 'text-accent-text font-semibold bg-surface-hover/50 before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-6 before:w-1 before:bg-accent before:rounded-full'
                      : 'text-ink-secondary hover:text-ink font-medium'
                  }`}
                >
                  {Icon && <Icon sx={{ fontSize: 20 }} className="shrink-0" />}
                  {!collapsed && <span className="truncate">{t(s.navKey)}</span>}
                </button>
              </li>
            );
          })}
        </ul>

        {!collapsed && <LanguageSwitcher />}
        <ThemeSwitcher collapsed={collapsed} />
        <CollapseToggle collapsed={collapsed} onToggle={onToggleCollapse} />
      </nav>
    </>
  );
}

function Topbar({ active, onOpenNav }) {
  const { t } = useTranslation();
  const section = SECTIONS.find((s) => s.id === active);

  return (
    <header className="h-12 flex items-center justify-between pl-14 pr-4 sm:pl-8 sm:pr-8 border-b border-border-subtle bg-surface flex-shrink-0">
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          className="sm:hidden flex h-9 w-9 items-center justify-center rounded-lg bg-surface-raised border border-border-strong text-ink shrink-0"
          onClick={onOpenNav}
          aria-label={t('ui:openNav')}
        >
          ☰
        </button>
        <span className="text-label text-ink-secondary uppercase tracking-widest truncate">
          {active !== 'graph' && section ? t(section.navKey) : ''}
        </span>
      </div>
      {active === 'graph' && (
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('refreshGraph'))}
          className="text-label text-ink-secondary hover:text-accent-text transition-colors tracking-widest uppercase"
        >
          {t('ui:refresh')}
        </button>
      )}
    </header>
  );
}

export default function App() {
  const { t } = useTranslation();
  const [active, setActive] = useState('graph');
  const [graphFocusNodeRef, setGraphFocusNodeRef] = useState(null);
  const [graphContextNodeId, setGraphContextNodeId] = useState(null);
  const [serviceStatusData, setServiceStatusData] = useState(null);
  const [traceabilityPrefill, setTraceabilityPrefill] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 768
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch (_) { return false; }
  });

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch (_) {}
      return next;
    });
  }, []);

  const refreshServiceStatus = useCallback(async () => {
    try {
      const data = await apiService.getStatus();
      setServiceStatusData(data);
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (active === 'status') refreshServiceStatus();
  }, [active, refreshServiceStatus]);

  // Jump to Trazabilidad and open a specific trace when the chat dispatches
  // neorag:openTrace. Lets a user see the full reasoning chain behind any
  // answer with one click.
  useEffect(() => {
    const handler = (e) => {
      const traceId = e?.detail?.traceId;
      if (!traceId) return;
      setTraceabilityPrefill({ traceId, at: Date.now() });
      setActive('traceability');
    };
    window.addEventListener('neorag:openTrace', handler);
    return () => window.removeEventListener('neorag:openTrace', handler);
  }, []);

  return (
    <div className="flex h-screen bg-surface text-ink font-sans overflow-hidden">
      <Sidebar
        active={active}
        onNavigate={setActive}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapse={toggleSidebarCollapsed}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Topbar active={active} onOpenNav={() => setSidebarOpen(true)} />

        <main
          role="main"
          className={`flex-1 min-h-0 ${
            active === 'graph' || active === 'chat' || active === 'traceability'
              ? 'overflow-hidden flex flex-col'
              : 'overflow-auto'
          }`}
        >
          {active === 'graph' && (
            <RDFView
              canvas={true}
              focusNodeRef={graphFocusNodeRef}
              onFocusNodeConsumed={() => setGraphFocusNodeRef(null)}
              onSelectionChange={setGraphContextNodeId}
            />
          )}

          {active === 'queries' && (
            <div className="h-full p-8">
              <SPARQLQuery apiUrl={apiService} />
            </div>
          )}

          {active === 'chat' && (
            <div className="flex-1 min-h-0 flex flex-col p-8">
              <ChatBox apiUrl={apiService} focusNodeId={graphContextNodeId} />
            </div>
          )}

          {active === 'etl' && (
            <div className="h-full p-8 flex flex-col min-h-0">
              <p className="text-label text-ink-muted mb-4 shrink-0">{t('content:etlSectionIntro')}</p>
              <div className="flex-1 min-h-0 overflow-auto">
                <ETLInterface />
              </div>
            </div>
          )}

          {active === 'articles' && (
            <div className="h-full p-8">
              <KGToText
                apiUrl={apiService}
                focusNodeId={graphContextNodeId}
                onOpenNodeInGraph={(ref) => {
                  setGraphFocusNodeRef(ref);
                  setActive('graph');
                }}
              />
            </div>
          )}

          {active === 'mapping' && (
            <div className="h-full p-8 overflow-auto flex flex-col">
              <p className="text-label text-ink-muted mb-4 shrink-0">{t('content:mappingSectionIntro')}</p>
              <div className="flex-1 min-h-0">
                <MappingTab />
              </div>
            </div>
          )}

          {active === 'traceability' && (
            <Traceability prefill={traceabilityPrefill} />
          )}

          {active === 'status' && (
            <div className="h-full p-8 overflow-auto flex flex-col">
              <p className="text-label text-ink-muted mb-4 shrink-0">{t('content:statusSectionIntro')}</p>
              <div className="flex-1 min-h-0 overflow-auto">
                <ServiceStatus serviceStatus={serviceStatusData} onRefresh={refreshServiceStatus} />
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
